import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { groupForecastApi } from "@/features/finances/api/groupForecast";
import type {
  ForecastSetEntry,
  EmployeeProjectStatusEntry,
} from "@/features/finances/types/groupForecast";

export const groupForecastKeys = {
  all: ["group-forecast"] as const,
  view: (year: number, month: number, horizon: number) =>
    [...groupForecastKeys.all, year, month, horizon] as const,
};

export function useGroupForecast(year: number, month: number, horizon = 12) {
  return useQuery({
    queryKey: groupForecastKeys.view(year, month, horizon),
    queryFn: () => groupForecastApi.get(year, month, horizon),
  });
}

export function useSetGroupForecast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: ForecastSetEntry[]) => groupForecastApi.set(entries),
    onSuccess: () => qc.invalidateQueries({ queryKey: groupForecastKeys.all }),
  });
}

export function useSalaryReconciliation(year: number, month: number) {
  return useQuery({
    queryKey: ["salary-reconciliation", year, month],
    queryFn: () => groupForecastApi.reconciliation(year, month),
  });
}

export function useContractorLayers() {
  return useQuery({
    queryKey: ["contractor-layers"],
    queryFn: () => groupForecastApi.contractorLayers(),
  });
}

export function useContractorGroupDetail(group: string | null) {
  return useQuery({
    queryKey: ["contractor-group-detail", group],
    queryFn: () => groupForecastApi.contractorGroupDetail(group as string),
    enabled: !!group,
  });
}

export function useNonProjectSmear(year: number, month: number) {
  return useQuery({
    queryKey: ["non-project-smear", year, month],
    queryFn: () => groupForecastApi.smear(year, month),
  });
}

export function useSetNonProjectSmear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: { group: string; fraction: string }[]) => groupForecastApi.setSmear(entries),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["non-project-smear"] });
      qc.invalidateQueries({ queryKey: ["salary-reconciliation"] });
    },
  });
}

export function useGroupSmearDetail(group: string | null, year: number, month: number, horizon = 12) {
  return useQuery({
    queryKey: ["group-smear-detail", group, year, month, horizon],
    queryFn: () => groupForecastApi.smearDetail(group as string, year, month, horizon),
    enabled: !!group,
  });
}

export function useSetProjectSuspension() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ project, periods }: { project: string; periods: import("@/features/finances/types/groupForecast").SuspensionPeriod[] }) =>
      groupForecastApi.setProjectSuspension(project, periods),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group-smear-detail"] });
      qc.invalidateQueries({ queryKey: ["non-project-smear"] });
      qc.invalidateQueries({ queryKey: groupForecastKeys.all });
    },
  });
}

export function useSetEmployeeProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: EmployeeProjectStatusEntry[]) => groupForecastApi.setEmployeeStatus(entries),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: groupForecastKeys.all });
      qc.invalidateQueries({ queryKey: ["salary-reconciliation"] });
    },
  });
}
